import { useState, useEffect } from "react";
import { trustService, TrustStats } from "@/services/trustService";
import { toast } from "@/hooks/use-toast";

export const useTrust = (userId?: string) => {
  const [trustStats, setTrustStats] = useState<TrustStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [trusting, setTrusting] = useState(false);

  // Load trust stats for a user
  useEffect(() => {
    if (userId) {
      loadTrustStats();
    }
  }, [userId]);

  const loadTrustStats = async () => {
    if (!userId) return;
    
    setLoading(true);
    try {
      const stats = await trustService.getTrustStats(userId);
      setTrustStats(stats);
    } catch (error) {
      console.error('Error loading trust stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const trustUser = async (comment?: string) => {
    if (!userId) return false;
    
    setTrusting(true);
    try {
      const success = await trustService.trustUser(userId, comment);
      if (success) {
        // Refresh trust stats
        await loadTrustStats();
        toast({
          title: "User Trusted",
          description: "You have successfully trusted this user.",
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to trust user. You may have already trusted them or don't have permission.",
          variant: "destructive"
        });
      }
      return success;
    } catch (error) {
      console.error('Error trusting user:', error);
      toast({
        title: "Error",
        description: "Failed to trust user.",
        variant: "destructive"
      });
      return false;
    } finally {
      setTrusting(false);
    }
  };

  return {
    trustStats,
    loading,
    trusting,
    trustUser,
    refreshStats: loadTrustStats
  };
};